import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lightbulb, ShoppingCart } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Empty } from "@/components/Empty";
import { Button } from "@/components/ui/button";
import { ADVICE_LABEL, ADVICE_TONE } from "@/lib/constants";
import { formatDate, formatNumber } from "@/lib/format";

const FILTERS = [
  { id: "", label: "Alle adviezen" },
  { id: "nu_bestellen", label: "Nu bestellen" },
  { id: "binnenkort", label: "Binnenkort bestellen" },
  { id: "in_bestelling", label: "Bestelling loopt al" },
  { id: "vertraagd", label: "Vertraagd" },
];

export default function Inkoopadvies() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => { api.get("/purchase-advice").then((r) => setData(r.data)); }, []);

  const advice = useMemo(() => {
    if (!data) return [];
    if (!filter) return data.advice;
    return data.advice.filter((a) => a.action === filter);
  }, [data, filter]);

  const counts = useMemo(() => {
    const c = { nu_bestellen: 0, binnenkort: 0, in_bestelling: 0, vertraagd: 0 };
    (data?.advice || []).forEach((a) => { if (c[a.action] !== undefined) c[a.action]++; });
    return c;
  }, [data]);

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="advice-page">
      <PageHeader
        eyebrow="Inkoopadvies"
        title="Wat moet er besteld worden?"
        description="Alleen producten waarvoor actie nodig is. Advies is gebaseerd op beschikbare voorraad, lopende bestellingen, verkoop en levertijd."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Nu bestellen" value={counts.nu_bestellen} tone="critical" />
        <StatCard label="Binnenkort bestellen" value={counts.binnenkort} tone="low" />
        <StatCard label="Bestelling loopt al" value={counts.in_bestelling} tone="in-transit" />
        <StatCard label="Vertraagd" value={counts.vertraagd} tone="critical" />
      </div>

      <div className="surface p-4 mb-6 flex flex-wrap gap-2 items-center" data-testid="advice-filter-bar">
        <span className="label-eyebrow">Filter</span>
        {FILTERS.map((opt) => (
          <Button key={opt.id || "all"} data-testid={`advice-filter-${opt.id || "all"}`}
            variant={filter === opt.id ? "default" : "outline"}
            onClick={() => setFilter(opt.id)} size="sm" className="h-10">{opt.label}</Button>
        ))}
      </div>

      <div className="surface overflow-hidden">
        <AdvisePanel data={data} advice={advice} nav={nav} />
      </div>
    </div>
  );
}

function AdvisePanel({ data, advice, nav }) {
  if (!data) return <div className="p-12 text-center text-muted-foreground">Advies laden…</div>;
  if (advice.length === 0) {
    return <Empty icon={Lightbulb} title="Alles onder controle"
      description="Er zijn geen producten die op dit moment bestelling nodig hebben." />;
  }
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-base">
        <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-4 py-4">Actie</th>
            <th className="px-4 py-4">Product</th>
            <th className="px-4 py-4">Artikel</th>
            <th className="px-4 py-4 text-right">Beschikbaar</th>
            <th className="px-4 py-4 text-right">Minimum</th>
            <th className="px-4 py-4 text-right">In bestelling</th>
            <th className="px-4 py-4 text-right">Gem. verkoop / week</th>
            <th className="px-4 py-4 text-right">Advies</th>
            <th className="px-4 py-4">Leverancier</th>
            <th className="px-4 py-4">Gewenste levering</th>
            <th className="px-4 py-4"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {advice.map((a) => (
            <tr key={a.product_id} data-testid={`advice-row-${a.article_number}`} className="hover:bg-accent">
              <td className="px-4 py-4"><StatusBadge tone={ADVICE_TONE[a.action]}>{ADVICE_LABEL[a.action]}</StatusBadge></td>
              <td className="px-4 py-4">
                <Link to={`/producten/${a.product_id}`} className="font-medium hover:text-primary">{a.product_name}</Link>
                <div className="text-xs text-muted-foreground">{a.category}</div>
              </td>
              <td className="px-4 py-4 font-mono text-sm">{a.article_number}</td>
              <td className="px-4 py-4 text-right font-mono">{formatNumber(a.available_stock, 1)} <span className="text-xs text-muted-foreground">{a.unit}</span></td>
              <td className="px-4 py-4 text-right font-mono text-muted-foreground">{formatNumber(a.minimum_stock, 0)}</td>
              <td className="px-4 py-4 text-right font-mono">
                {a.open_ordered > 0 ? <span className="text-status-in-transit">{formatNumber(a.open_ordered, 0)}</span> : "—"}
              </td>
              <td className="px-4 py-4 text-right font-mono text-muted-foreground">{formatNumber(a.weekly_sales_avg, 1)}</td>
              <td className="px-4 py-4 text-right font-mono font-semibold text-status-critical">{formatNumber(a.recommended_quantity, 0)} {a.unit}</td>
              <td className="px-4 py-4 text-muted-foreground truncate max-w-[160px]">{a.supplier_name}</td>
              <td className="px-4 py-4 text-muted-foreground">{formatDate(a.desired_delivery_date)}</td>
              <td className="px-4 py-4 text-right">
                {(a.action === "nu_bestellen" || a.action === "binnenkort") && (
                  <Button size="sm" data-testid={`advice-create-${a.article_number}`}
                    onClick={() => nav(`/bestellingen/nieuw?product=${a.product_id}&qty=${a.recommended_quantity}${a.supplier_id ? `&supplier=${a.supplier_id}` : ""}`)} className="h-10">
                    <ShoppingCart className="w-4 h-4 mr-2" /> Bestellen
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const toneCls = { critical: "border-status-critical/40", low: "border-status-low/50",
    "in-transit": "border-status-in-transit/40" }[tone];
  return (
    <div className={`surface p-5 ${toneCls}`}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-mono text-3xl font-semibold mt-2">{value}</div>
    </div>
  );
}
